import { z } from "@core/zod";

const GithubReviewCommentPayloadSchema = z.object(
  {
    action: z.enum(["created", "deleted", "edited"]),
    comment: z.object(
      {
        body: z.string(),
        created_at: z.iso.datetime(),
        diff_hunk: z.string().nullable(),
        html_url: z.url(),
        id: z.int().nonnegative(),
        in_reply_to_id: z.int().nonnegative().optional(),
        path: z.string(),
        updated_at: z.iso.datetime(),
        user: z.object(
          { login: z.string(), type: z.string() },
          {
            login: "The author's GitHub login.",
            type: "The type of the author's GitHub account.",
          },
        ),
      },
      {
        body: "The comment's Markdown body.",
        created_at: "When GitHub records the comment as created.",
        diff_hunk: "The diff hunk GitHub showed the reviewer, or null.",
        html_url: "The comment's URL on GitHub.",
        id: "The comment's GitHub id.",
        in_reply_to_id: "The GitHub id of the comment this one replies to.",
        path: "The path of the file the comment is on.",
        updated_at: "When GitHub records the comment as updated.",
        user: "The comment's author.",
      },
    ),
    pull_request: z.object(
      { number: z.int().nonnegative() },
      { number: "The pull request's number." },
    ),
    repository: z.object(
      { id: z.int().nonnegative() },
      { id: "The repo's GitHub id." },
    ),
  },
  {
    action: "What happened to the comment: created, deleted, or edited.",
    comment: "The review comment the event concerns.",
    pull_request: "The pull request the comment is on.",
    repository: "The repo the pull request belongs to.",
  },
);

export type GithubReviewCommentPayload = z.infer<
  typeof GithubReviewCommentPayloadSchema
>;

export const GithubWebhookDeliverySchema = z.discriminatedUnion("event", [
  z.object(
    {
      deliveryId: z.string(),
      event: z.literal("pull_request_review_comment"),
      payload: GithubReviewCommentPayloadSchema,
    },
    {
      deliveryId: "The id GitHub gave the webhook delivery.",
      event: "The name of the event GitHub delivered.",
      payload: "The event's payload, in the shape GitHub sends.",
    },
  ),
]);

export type GithubWebhookDelivery = z.infer<typeof GithubWebhookDeliverySchema>;
